<template>
  <!-- No destination: render a plain wrapper -->
  <span v-if="!to" class="app-no-link" :class="{ 'app-no-link--show': $slots.default }">
    <slot />
  </span>

  <!-- External destination: plain <a> -->
  <a
    v-else-if="isExternal"
    v-bind="$attrs"
    :href="to"
    :aria-label="ariaLabel ? ariaLabel : null"
    :rel="isHttpLink ? 'noopener noreferrer' : null"
    :target="isHttpLink && openInAnotherTab ? '_blank' : null"
    @click.left="onClick"
    @click.middle="onClick"
    @click.right="onClick"
  >
    <slot />
  </a>

  <!-- Internal destination: router link -->
  <RouterLink v-else v-slot="{ href, navigate, isActive, isExactActive }" :to="internalRoute" :replace="replace" custom>
    <a
      :aria-current="ariaCurrentValue"
      :aria-label="ariaLabel ? ariaLabel : null"
      :class="{
        [activeClass]: isActive,
        [exactActiveClass]: isExactActive,
      }"
      :href="href"
      :target="openInAnotherTab ? '_blank' : null"
      v-bind="$attrs"
      @click.left="onRouterLinkClick($event, navigate)"
      @click.middle="onRouterLinkClick($event, navigate)"
      @click.right="onRouterLinkClick($event, navigate)"
    >
      <slot />
    </a>
  </RouterLink>
</template>

<script>
import { sendGaEvent } from '@/helpers/ga/gaSend';
import GaEvent from '@/helpers/ga/events/GaEvent';
import AppConfig from '@/config';

export default {
  name: 'AppLink',
  inheritAttrs: false,

  props: {
    activeClass: {
      type: String,
      default: 'router-link-active',
    },
    exactActiveClass: {
      type: String,
      default: 'router-link-exact-active',
    },
    ariaCurrentValue: {
      type: String,
      default: 'page',
    },
    ariaLabel: {
      type: String,
      default: null,
    },
    /**
     * Force rendering as external (<a>) or internal (<RouterLink>).
     * When omitted (null), the type is inferred from `to`.
     */
    external: {
      type: Boolean,
      default: null,
    },
    gaEvent: {
      type: Object,
      default: null,
      validator: (value) => {
        if (value === null) {
          return true;
        }
        return typeof value === 'object' && value.name && value.params !== null && typeof value.params === 'object';
      },
    },
    openInAnotherTab: {
      type: Boolean,
      default: false,
    },
    /** Only for internal links (<RouterLink>) */
    replace: {
      type: Boolean,
      default: false,
    },
    /** Route object, internal path ("/about") or external URL ("https://…", "mailto:…") */
    to: {
      type: [Object, String],
      default: null,
    },
  },

  computed: {
    /**
     * External links render as a plain <a>.
     * Explicit `external` prop wins; otherwise infer it:
     * route objects, paths starting with "/", and absolute URLs pointing
     * at this app (baseUrl) are internal — everything else is external.
     */
    isExternal() {
      if (this.external !== null) {
        return this.external;
      }

      if (typeof this.to !== 'string') {
        return false;
      }

      return !(this.to.startsWith('/') || this.to.startsWith(AppConfig.getAsString('baseUrl', '')));
    },

    /** True for http(s) URLs — these get rel="noopener noreferrer" and may open in a new tab */
    isHttpLink() {
      return typeof this.to === 'string' && this.to.startsWith('http');
    },

    /**
     * Destination for internal links, normalized to a route object:
     * - strips the app base URL so absolute URLs still resolve via the router
     * - carries the current `?token` query param across navigation, if present
     */
    internalRoute() {
      const route =
        typeof this.to === 'string' ? { path: this.to.replace(AppConfig.getAsString('baseUrl', ''), '') } : this.to;

      const { token } = this.$route.query;
      if (!token) {
        return route;
      }

      return { ...route, query: { ...route.query, token } };
    },
  },

  methods: {
    onClick(e) {
      if (this.gaEvent) {
        const gaEvent = GaEvent.fromPlainObject(this.gaEvent);
        if (!gaEvent.hasCtaName()) {
          gaEvent.addCtaName(e.target.innerText);
        }
        sendGaEvent(gaEvent);
      }
      this.$emit('click', e);
    },
    onRouterLinkClick(e, navigate) {
      this.onClick(e);

      // bail out if the consumer (or default-prevented) wants to stop navigation
      if (e.defaultPrevented) {
        return;
      }

      navigate(e);
    },
  },
};
</script>

<style lang="scss" scoped>
.app-no-link {
  cursor: default;

  &:not(&--show) {
    visibility: hidden;
  }
}
</style>
